/**
 * Session-scoped navigation state for Observation Task Administration.
 *
 * WHY THIS EXISTS
 * ----------------
 * Task Admin keeps the active task / Self-Report panel in React state. A full
 * page refresh remounts the route and used to always land on Task 1, even when
 * the clinician had been mid-way through Task 3. Restoring the last-viewed tab
 * for the current browser session matches how the clinician dashboard keeps
 * filters across navigation.
 *
 * WHAT WE PERSIST (and what we deliberately do not)
 * -------------------------------------------------
 * Persist only navigation: which task or general panel is on screen.
 * Never persist clinical notes, evidence decisions, dirty flags, or anything
 * that could trigger Firestore writes or AI paragraph regeneration on restore.
 * Those concerns stay owned by useObservationStageData + the exit-regen nets.
 *
 * KEYING
 * ------
 * Keys are patient-scoped (`td.obs.v1.taskAdminNav.<patientId>`) so two
 * patients open in the same tab never inherit each other's place. Bump the
 * `v1` segment if the stored shape changes incompatibly.
 *
 * sessionStorage (via useSessionState) clears when the tab closes — a natural
 * fit for "where I was in this appointment today" rather than a durable
 * preference.
 */

/** Prefix shared by every Task Admin nav key. Bump on incompatible shape changes. */
export const OBS_TASK_ADMIN_NAV_PREFIX = 'td.obs.v1.taskAdminNav.';

/**
 * @param {string} patientId
 * @returns {string} sessionStorage key for this patient's Task Admin nav
 */
export function getObsTaskAdminNavKey(patientId) {
  return `${OBS_TASK_ADMIN_NAV_PREFIX}${patientId || 'unknown'}`;
}

/**
 * Default nav when nothing useful is stored (or the stored target is gone).
 *
 * @param {Array<number|string>} selectedTaskIds
 * @returns {{ activeTaskId: number|string|null, activeView: string, activeGeneralPanel: string|null }}
 */
export function defaultObsTaskAdminNav(selectedTaskIds = []) {
  const ids = Array.isArray(selectedTaskIds) ? selectedTaskIds : [];
  return {
    activeTaskId: ids.length > 0 ? ids[0] : null,
    activeView: ids.length > 0 ? 'task' : 'picker',
    activeGeneralPanel: null,
  };
}

/**
 * Validate a stored nav snapshot against the patient's current task list.
 *
 * If the clinician removed the task they were on, or the Self-Report panel is
 * no longer a sensible target, fall back to a safe default rather than showing
 * a blank / orphaned view.
 *
 * @param {Object|null|undefined} stored - value from sessionStorage
 * @param {Array<number|string>} selectedTaskIds - live selected tasks
 * @param {string[]} [validGeneralPanelIds=['self_reported']] - panels shown in Task Admin
 * @returns {{ activeTaskId: number|string|null, activeView: string, activeGeneralPanel: string|null }}
 */
export function resolveObsTaskAdminNav(
  stored,
  selectedTaskIds = [],
  validGeneralPanelIds = ['self_reported'],
) {
  const fallback = defaultObsTaskAdminNav(selectedTaskIds);
  if (!stored || typeof stored !== 'object') return fallback;

  const ids = Array.isArray(selectedTaskIds) ? selectedTaskIds : [];
  const view = stored.activeView;
  const taskId = stored.activeTaskId;
  const panelId = stored.activeGeneralPanel;

  // Self-Report (or another general panel) — keep the panel if still valid.
  // Keep a remembered task id only when it is still selected (handy for
  // returning to tasks after leaving Self-Report).
  if (view === 'general' && validGeneralPanelIds.includes(panelId)) {
    return {
      activeTaskId: ids.includes(taskId) ? taskId : fallback.activeTaskId,
      activeView: 'general',
      activeGeneralPanel: panelId,
    };
  }

  // Explicit task-picker view (no active task content).
  if (view === 'picker') {
    return {
      activeTaskId: ids.includes(taskId) ? taskId : fallback.activeTaskId,
      activeView: 'picker',
      activeGeneralPanel: null,
    };
  }

  // Task view — only restore when that task is still on the appointment.
  if (view === 'task' && ids.includes(taskId)) {
    return {
      activeTaskId: taskId,
      activeView: 'task',
      activeGeneralPanel: null,
    };
  }

  return fallback;
}
